import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Save, Download, Trash2, FileText, CheckCircle2,
  AlertCircle, Edit2, Copy, ChevronDown, ChevronUp, RefreshCw, Send,
  FileSignature, X, GripVertical, ClipboardList
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  review: { label: 'In Review', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  superseded: { label: 'Superseded', color: 'bg-amber-100 text-amber-700', icon: FileText },
};

export default function ScopeStatementEditor() {
  const { projectId, statementId } = useParams();
  const navigate = useNavigate();
  const isNew = !statementId || statementId === 'new'; // Handle both undefined and 'new'
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [project, setProject] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  
  const [statement, setStatement] = useState({
    name: 'Project Scope Statement',
    version: 1,
    status: 'draft',
    prepared_by: '',
    prepared_date: new Date().toISOString().split('T')[0],
    client_name: '',
    project_address: '',
    // Main sections matching the user's template
    justification: '',
    goals: [],
    deliverables: [],
    expected_results: [],
    assumptions: [],
    constraints: [],
    included_work: [],
    excluded_work: [],
    // Legacy fields for backward compatibility
    project_overview: '',
    project_objectives: [],
    allowances: [],
    rooms_in_scope: [],
    estimated_duration: '',
    key_milestones: [],
    budget_summary: '',
    payment_schedule: [],
    custom_sections: [],
    requires_signature: true,
    signatures: [],
    source_survey_id: null,
  });
  
  const [expandedSections, setExpandedSections] = useState({
    justification: true,
    goals: true,
    deliverables: true,
    expected_results: true,
    assumptions: true,
    constraints: true,
    inclusions: true,
    timeline: false,
    budget: false,
    custom: false,
  });
  
  const [newItem, setNewItem] = useState('');
  const [editingField, setEditingField] = useState(null);
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const checkDarkMode = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    checkDarkMode();
    window.addEventListener('storage', checkDarkMode);
    return () => window.removeEventListener('storage', checkDarkMode);
  }, []);

  useEffect(() => {
    loadData();
  }, [projectId, statementId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectRes = await axios.get(`${API}/api/projects/${projectId}`);
      setProject(projectRes.data);
      
      // Load surveys for this project
      try {
        const surveysRes = await axios.get(`${API}/api/projects/${projectId}/surveys`);
        setSurveys(surveysRes.data.filter(s => s.status === 'completed'));
      } catch (e) {
        console.log('No surveys found');
      }
      
      if (!isNew && statementId) {
        // Load existing statement only if we have a valid ID
        const statementRes = await axios.get(`${API}/api/projects/${projectId}/scope-statements/${statementId}`);
        // Map custom_sections to individual fields if present
        const data = statementRes.data;
        if (data.custom_sections && data.custom_sections.length > 0) {
          data.custom_sections.forEach(section => {
            const key = section.title?.toLowerCase().replace(/\s+/g, '_');
            if (key && section.content) {
              // Parse bullet points back to arrays
              const lines = section.content.split('\n')
                .filter(l => l.trim())
                .map(l => l.replace(/^[•\-]\s*/, '').trim())
                .filter(l => l);
              if (['goals', 'deliverables', 'expected_results', 'assumptions', 'constraints'].includes(key)) {
                data[key] = lines;
              } else if (key === 'justification') {
                data.justification = section.content;
              }
            }
          });
        }
        setStatement(data);
      } else {
        // Pre-fill from project for new statements
        setStatement(prev => ({
          ...prev,
          client_name: projectRes.data.client_name || '',
          project_address: formatAddress(projectRes.data.site_address),
          project_overview: projectRes.data.description || '',
        }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    const parts = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean);
    return parts.join(', ');
  };

  const handleImportFromSurvey = async (surveyId) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/api/projects/${projectId}/scope-statements/generate-from-survey`, {
        survey_id: surveyId
      });
      setStatement(response.data.statement);
      toast.success('Scope statement generated from survey!');
      setShowSurveyDialog(false);
      
      // Navigate to the new statement
      if (isNew) {
        navigate(`/projects/${projectId}/scope-statements/${response.data.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Error generating from survey:', error);
      toast.error('Failed to generate from survey');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/api/projects/${projectId}/scope-statements/generate`);
      setStatement(response.data.statement);
      toast.success('Scope statement generated from project data');
      
      // Navigate to the new statement
      if (isNew) {
        navigate(`/projects/${projectId}/scope-statements/${response.data.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Error generating:', error);
      toast.error('Failed to generate scope statement');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew || !statement.id) {
        const response = await axios.post(`${API}/api/projects/${projectId}/scope-statements`, statement);
        toast.success('Scope statement created');
        navigate(`/projects/${projectId}/scope-statements/${response.data.id}`, { replace: true });
      } else {
        await axios.put(`${API}/api/projects/${projectId}/scope-statements/${statement.id}`, statement);
        toast.success('Scope statement saved');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save scope statement');
    } finally {
      setSaving(true);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this scope statement?')) return;
    
    try {
      await axios.delete(`${API}/api/projects/${projectId}/scope-statements/${statement.id}`);
      toast.success('Scope statement deleted');
      navigate(`/projects/${projectId}/hub?tab=documents`);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete scope statement');
    }
  };

  const updateField = (field, value) => {
    setStatement(prev => ({ ...prev, [field]: value }));
  };

  const addListItem = (field) => {
    if (!newItem.trim()) return;
    setStatement(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), newItem.trim()]
    }));
    setNewItem('');
  };

  const removeListItem = (field, index) => {
    setStatement(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const cardClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]';
  const inputClass = darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : '';
  const textClass = darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]';
  const mutedClass = darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[statement.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/projects/${projectId}/hub?tab=documents`)}
              className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className={`text-xl font-heading ${textClass}`}>
                  {statement.name || 'New Scope Statement'}
                </h1>
                <Badge className={statusConfig.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
                {statement.version > 1 && (
                  <Badge variant="outline">v{statement.version}</Badge>
                )}
              </div>
              <p className={`text-sm ${mutedClass}`}>
                {project?.name} • {statement.client_name || 'Client'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isNew && surveys.length > 0 && (
              <Button 
                variant="outline" 
                onClick={() => setShowSurveyDialog(true)}
                disabled={generating}
                className={`text-[#BC5D41] border-[#BC5D41] hover:bg-[#BC5D41]/10 ${darkMode ? '' : ''}`}
                data-testid="import-survey-btn"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                Import from Survey
              </Button>
            )}
            {isNew && (
              <Button 
                variant="outline" 
                onClick={handleGenerate}
                disabled={generating}
                className={darkMode ? 'border-[#2A2A2A]' : ''}
                data-testid="auto-generate-btn"
              >
                {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Auto-Generate
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={handleDelete}
              disabled={isNew}
              className={`text-red-600 hover:text-red-700 ${darkMode ? 'border-[#2A2A2A]' : ''}`}
              data-testid="delete-scope-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
              data-testid="save-scope-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      {/* Survey Import Dialog */}
      <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
        <DialogContent className={`max-w-lg ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>Import from Client Survey</DialogTitle>
            <DialogDescription className={mutedClass}>
              Select a completed survey to generate the scope statement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {surveys.map(s => (
              <div
                key={s.id}
                className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-[#BC5D41] ${
                  darkMode ? 'border-[#2A2A2A] hover:bg-[#2A2A2A]' : 'border-[#E5E2DD] hover:bg-[#FAF9F7]'
                }`}
                onClick={() => handleImportFromSurvey(s.id)}
                data-testid={`survey-option-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-medium ${textClass}`}>{s.title}</h4>
                    <p className={`text-sm ${mutedClass}`}>
                      Completed {s.completed_at ? new Date(s.completed_at).toLocaleDateString() : 'Recently'}
                    </p>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Completed</Badge>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSurveyDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Breadcrumbs */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'bg-white'} px-6 py-2`}>
        <div className="flex items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}>
            My Desk
          </Button>
          <span className={mutedClass}>/</span>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}/hub`)} className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}>
            {project?.name || 'Project'}
          </Button>
          <span className={mutedClass}>/</span>
          <span className={textClass}>Scope Statement</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {/* Document Info */}
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${textClass}`}>
              <FileText className="w-5 h-5 text-[#BC5D41]" />
              Document Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div>
              <Label className={mutedClass}>Document Name</Label>
              <Input
                value={statement.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <Label className={mutedClass}>Status</Label>
              <Select value={statement.status} onValueChange={(v) => updateField('status', v)}>
                <SelectTrigger className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="review">In Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="superseded">Superseded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={mutedClass}>Prepared By</Label>
              <Input
                value={statement.prepared_by}
                onChange={(e) => updateField('prepared_by', e.target.value)}
                placeholder="Company or designer name"
                className={inputClass}
              />
            </div>
            <div>
              <Label className={mutedClass}>Prepared Date</Label>
              <Input
                type="date"
                value={statement.prepared_date}
                onChange={(e) => updateField('prepared_date', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <Label className={mutedClass}>Client Name</Label>
              <Input
                value={statement.client_name}
                onChange={(e) => updateField('client_name', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <Label className={mutedClass}>Project Address</Label>
              <Input
                value={statement.project_address}
                onChange={(e) => updateField('project_address', e.target.value)}
                className={inputClass}
              />
            </div>
          </CardContent>
        </Card>

        {/* 1. Justification Section */}
        <Collapsible open={expandedSections.justification} onOpenChange={() => toggleSection('justification')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  📋 Justification
                </CardTitle>
                {expandedSections.justification ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-2">
                <Label className={mutedClass}>Why is the project happening in the first place? Briefly describe the client&apos;s reason for undergoing this project.</Label>
                <Textarea
                  value={statement.justification || statement.project_overview || ''}
                  onChange={(e) => updateField('justification', e.target.value)}
                  placeholder="Describe the client's motivation and reasons for this project..."
                  rows={5}
                  className={inputClass}
                  data-testid="justification-input"
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 2. Goals Section */}
        <Collapsible open={expandedSections.goals} onOpenChange={() => toggleSection('goals')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  🎯 Goals
                </CardTitle>
                {expandedSections.goals ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-2">
                <Label className={mutedClass}>What is the client seeking to achieve?</Label>
                <div className="space-y-2 mt-2">
                  {(statement.goals || statement.project_objectives || []).map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-[#2A2A2A]' : 'bg-[#FAF9F7]'}`}>
                      <span className={`flex-1 text-sm ${textClass}`}>• {item}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('goals', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={editingField === 'goals' ? newItem : ''}
                      onChange={(e) => { setNewItem(e.target.value); setEditingField('goals'); }}
                      placeholder="Add a goal..."
                      className={`flex-1 ${inputClass}`}
                      onKeyPress={(e) => e.key === 'Enter' && addListItem('goals')}
                      data-testid="add-goal-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => addListItem('goals')}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 3. Deliverables Section */}
        <Collapsible open={expandedSections.deliverables} onOpenChange={() => toggleSection('deliverables')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  📦 Deliverables
                </CardTitle>
                {expandedSections.deliverables ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-2">
                <Label className={mutedClass}>A breakdown of deliverables and who will be responsible for them.</Label>
                <div className="space-y-2 mt-2">
                  {(statement.deliverables || []).map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-[#2A2A2A]' : 'bg-[#FAF9F7]'}`}>
                      <span className={`flex-1 text-sm ${textClass}`}>• {item}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('deliverables', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={editingField === 'deliverables' ? newItem : ''}
                      onChange={(e) => { setNewItem(e.target.value); setEditingField('deliverables'); }}
                      placeholder="Add a deliverable..."
                      className={`flex-1 ${inputClass}`}
                      onKeyPress={(e) => e.key === 'Enter' && addListItem('deliverables')}
                      data-testid="add-deliverable-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => addListItem('deliverables')}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 4. Expected Results Section */}
        <Collapsible open={expandedSections.expected_results} onOpenChange={() => toggleSection('expected_results')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  ✨ Expected Results
                </CardTitle>
                {expandedSections.expected_results ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-2">
                <Label className={mutedClass}>When the project is complete, what will change? Think about your original justification here and note the key improvements.</Label>
                <div className="space-y-2 mt-2">
                  {(statement.expected_results || []).map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-[#2A2A2A]' : 'bg-[#FAF9F7]'}`}>
                      <span className={`flex-1 text-sm ${textClass}`}>• {item}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('expected_results', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={editingField === 'expected_results' ? newItem : ''}
                      onChange={(e) => { setNewItem(e.target.value); setEditingField('expected_results'); }}
                      placeholder="Add expected result..."
                      className={`flex-1 ${inputClass}`}
                      onKeyPress={(e) => e.key === 'Enter' && addListItem('expected_results')}
                      data-testid="add-result-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => addListItem('expected_results')}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 5. Assumptions Section */}
        <Collapsible open={expandedSections.assumptions} onOpenChange={() => toggleSection('assumptions')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  💭 Assumptions
                </CardTitle>
                {expandedSections.assumptions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-2">
                <Label className={mutedClass}>What are you relying on to make your project happen? Consider the availability of teams, budgets, materials, training, etc.</Label>
                <div className="space-y-2 mt-2">
                  {(statement.assumptions || []).map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
                      <span className={`flex-1 text-sm ${textClass}`}>• {item}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('assumptions', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={editingField === 'assumptions' ? newItem : ''}
                      onChange={(e) => { setNewItem(e.target.value); setEditingField('assumptions'); }}
                      placeholder="Add an assumption..."
                      className={`flex-1 ${inputClass}`}
                      onKeyPress={(e) => e.key === 'Enter' && addListItem('assumptions')}
                      data-testid="add-assumption-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => addListItem('assumptions')}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 6. Constraints Section */}
        <Collapsible open={expandedSections.constraints} onOpenChange={() => toggleSection('constraints')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  ⚠️ Constraints
                </CardTitle>
                {expandedSections.constraints ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-2">
                <Label className={mutedClass}>What are obstacles to make note of?</Label>
                <div className="space-y-2 mt-2">
                  {(statement.constraints || []).map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className={`flex-1 text-sm ${textClass}`}>{item}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('constraints', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={editingField === 'constraints' ? newItem : ''}
                      onChange={(e) => { setNewItem(e.target.value); setEditingField('constraints'); }}
                      placeholder="Add a constraint..."
                      className={`flex-1 ${inputClass}`}
                      onKeyPress={(e) => e.key === 'Enter' && addListItem('constraints')}
                      data-testid="add-constraint-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => addListItem('constraints')}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 7. Inclusions and Exclusions Section */}
        <Collapsible open={expandedSections.inclusions} onOpenChange={() => toggleSection('inclusions')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  ✅❌ Inclusions and Exclusions
                </CardTitle>
                {expandedSections.inclusions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                <Label className={mutedClass}>Finally, what is in and out of the scope of your project? It&apos;s good to drill down into specifics here.</Label>
                
                {/* Included Work */}
                <div>
                  <Label className={`${mutedClass} flex items-center gap-2 mb-2`}>
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Work Included in Scope
                  </Label>
                  <div className="space-y-2">
                    {(statement.included_work || []).map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className={`flex-1 text-sm ${textClass}`}>{item}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('included_work', idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={editingField === 'included_work' ? newItem : ''}
                        onChange={(e) => { setNewItem(e.target.value); setEditingField('included_work'); }}
                        placeholder="Add included item..."
                        className={`flex-1 ${inputClass}`}
                        onKeyPress={(e) => e.key === 'Enter' && addListItem('included_work')}
                        data-testid="add-inclusion-input"
                      />
                      <Button variant="outline" size="sm" onClick={() => addListItem('included_work')}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Excluded Work */}
                <div>
                  <Label className={`${mutedClass} flex items-center gap-2 mb-2`}>
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Work Excluded from Scope
                  </Label>
                  <div className="space-y-2">
                    {(statement.excluded_work || []).map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                        <X className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <span className={`flex-1 text-sm ${textClass}`}>{item}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeListItem('excluded_work', idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={editingField === 'excluded_work' ? newItem : ''}
                        onChange={(e) => { setNewItem(e.target.value); setEditingField('excluded_work'); }}
                        placeholder="Add excluded item..."
                        className={`flex-1 ${inputClass}`}
                        onKeyPress={(e) => e.key === 'Enter' && addListItem('excluded_work')}
                        data-testid="add-exclusion-input"
                      />
                      <Button variant="outline" size="sm" onClick={() => addListItem('excluded_work')}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Budget & Timeline */}
        <Collapsible open={expandedSections.budget} onOpenChange={() => toggleSection('budget')}>
          <Card className={cardClass}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  💰 Budget & Timeline
                </CardTitle>
                {expandedSections.budget ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4">
                <div>
                  <Label className={mutedClass}>Budget Summary</Label>
                  <Textarea
                    value={statement.budget_summary}
                    onChange={(e) => updateField('budget_summary', e.target.value)}
                    placeholder="Budget overview, payment terms, etc."
                    rows={3}
                    className={inputClass}
                    data-testid="budget-input"
                  />
                </div>
                <div>
                  <Label className={mutedClass}>Estimated Duration</Label>
                  <Input
                    value={statement.estimated_duration}
                    onChange={(e) => updateField('estimated_duration', e.target.value)}
                    placeholder="e.g., 8-12 weeks"
                    className={inputClass}
                    data-testid="duration-input"
                  />
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Signature Section */}
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${textClass}`}>
              <FileSignature className="w-5 h-5 text-[#BC5D41]" />
              Signatures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                checked={statement.requires_signature}
                onCheckedChange={(checked) => updateField('requires_signature', checked)}
                data-testid="signature-checkbox"
              />
              <Label className={mutedClass}>Requires client signature for approval</Label>
            </div>
            {statement.requires_signature && (
              <p className={`text-sm ${mutedClass}`}>
                Signature fields will be added when the document is sent for approval.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
